import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { 
  FaClock, 
  FaCalendarAlt, 
  FaUsers, 
  FaStar,
  FaCheckCircle,
  FaBookOpen,
  FaCertificate,
  FaArrowRight,
  FaHospital,
  FaProcedures,
  FaChartLine,
  FaFileInvoice,
  FaStethoscope,
  FaMicroscope,
  FaHeartbeat,
  FaUserMd,
  FaClipboardCheck,
  FaDollarSign,
  FaShieldAlt
} from 'react-icons/fa';

const PageContainer = styled.div`
  margin-top: 200px;
  min-height: calc(100vh - 210px);
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);


  @media (max-width: 768px) {
    margin-top: 120px;
  }
`;

const HeroSection = styled.div`
  background: linear-gradient(135deg, #0a4d68 0%, #088395 50%, #0a2b3c 100%);
  color: white;
  padding: 4rem 5%;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50px;
    right: -50px;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, rgba(255, 217, 102, 0.1) 0%, transparent 70%);
    border-radius: 50%;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -50px;
    left: -50px;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, rgba(255, 217, 102, 0.1) 0%, transparent 70%);
    border-radius: 50%;
  }
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  margin-bottom: 1rem;
  position: relative;
  z-index: 2;
  
  span {
    color: #ffd966;
    display: block;
    font-size: 2rem;
    font-weight: 300;
    margin-top: 0.5rem;
  }
`;

const HeroDescription = styled.p`
  font-size: 1.2rem;
  max-width: 700px;
  margin: 0 auto 2rem;
  opacity: 0.95;
  position: relative;
  z-index: 2;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 4rem 5%;
`;

const SectionTitle = styled.h2`
  font-size: 2.2rem;
  color: #0a2b3c;
  margin-bottom: 2rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 80px;
    height: 4px;
    background: #ffd966;
    border-radius: 2px;
  }
`;

const Grid2Col = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;
  
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Grid3Col = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  
  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const ContentBlock = styled.div`
  p {
    color: #4a5f73;
    line-height: 1.8;
    margin-bottom: 1.5rem;
    font-size: 1.1rem;
  }
`;

const InfoCard = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(10, 77, 104, 0.05);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(10, 77, 104, 0.1);
    border-color: #ffd966;
  }
`;

const IconWrapper = styled.div`
  font-size: 2.5rem;
  color: #ffd966;
  margin-bottom: 1.5rem;
`;

const CardTitle = styled.h3`
  color: #0a2b3c;
  font-size: 1.3rem;
  margin-bottom: 1rem;
  font-weight: 600;
`;

const CardText = styled.p`
  color: #5a6b7a;
  line-height: 1.6;
  font-size: 0.95rem;
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
`;

const ListItem = styled.li`
  margin-bottom: 0.8rem;
  color: #4a5f73;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  
  svg {
    color: #ffd966;
    font-size: 1rem;
  }
`;



const StatCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 10px;
  text-align: center;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(10, 77, 104, 0.05);
  
  .value {
    font-size: 2.2rem;
    font-weight: 700;
    color: #0a4d68;
    margin-bottom: 0.3rem;
  }
  
  .label {
    color: #5a6b7a;
    font-size: 0.9rem;
    text-transform: uppercase;
  }
`;

const CurriculumSection = styled.div`
  margin: 3rem 0;
`;

const ModuleCard = styled.div`
  background: white;
  border-radius: 12px;
  margin-bottom: 1rem;
  border: 1px solid rgba(10, 77, 104, 0.05);
  overflow: hidden;
`;

const ModuleHeader = styled.div`
  background: #f8fafc;
  padding: 1.2rem 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  
  h3 {
    color: #0a2b3c;
    font-size: 1.2rem;
    margin: 0;
  }
`;

const ModuleContent = styled.div`
  padding: 1.5rem;
`;

const CTASection = styled.div`
  background: linear-gradient(135deg, #0a4d68, #088395);
  border-radius: 20px;
  padding: 3rem;
  text-align: center;
  color: white;
  margin-top: 4rem;
  
  h3 {
    font-size: 2rem;
    margin-bottom: 1rem;
  }
  
  p {
    font-size: 1.1rem;
    margin-bottom: 2rem;
    opacity: 0.9;
  }
`;

const CTAButton = styled(Link)`
  background: #ffd966;
  color: #0a4d68;
  border: none;
  padding: 1rem 3rem;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  
  &:hover {
    background: white;
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  }
`;

const AmbulatorySurgery = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);



  const curriculum = [
    {
      module: 1,
      title: "Ambulatory Surgery Center (ASC) Overview",
      topics: [
        "ASC vs Hospital Outpatient Department",
        "Medicare coverage for ASCs",
        "Covered procedures list",
        "Payment rates and methodology"
      ]
    },
    {
      module: 2,
      title: "CPT Coding for ASC Procedures",
      topics: [
        "Surgical package definition",
        "Modifier usage in ASC setting",
        "Multiple procedure discounting",
        "Unlisted procedures"
      ]
    },
    {
      module: 3,
      title: "ICD-10-CM Coding for ASCs",
      topics: [
        "Diagnosis coding for outpatient surgery",
        "Laterality and specificity",
        "Complication coding",
        "Medical necessity determination"
      ]
    },
    {
      module: 4,
      title: "Billing & Reimbursement",
      topics: [
        "CMS-1500 claim form",
        "HCPCS Level II codes",
        "Medicare Administrative Contractors",
        "Appeals and reconsiderations"
      ]
    }
  ];

  return (
    <PageContainer>
      <HeroSection>
        <HeroTitle>
          Ambulatory Surgery Center Coding
          <span>Master ASC Billing & Coding</span>
        </HeroTitle>
        <HeroDescription>
          Specialized training for ambulatory surgery center coding, billing, and reimbursement. 
          Learn the unique requirements of ASCs and maximize your coding accuracy.
        </HeroDescription>
      </HeroSection>

      <Container>
        

        <Grid2Col>
          <ContentBlock>
            <SectionTitle>Program Overview</SectionTitle>
            <p>
              Ambulatory Surgery Centers (ASCs) have unique coding and billing requirements that 
              differ from hospital outpatient departments. This comprehensive program is designed 
              to provide coders, billers, and healthcare professionals with the specialized knowledge 
              needed to accurately code and bill for ASC procedures.
            </p>
            <p>
              You'll learn the intricacies of ASC payment methodology, covered procedures, modifier 
              usage, and compliance requirements. Our expert instructors bring real-world experience 
              to help you master ASC coding and maximize reimbursement while maintaining compliance.
            </p>
          </ContentBlock>
          
          <InfoCard>
            <IconWrapper><FaHospital /></IconWrapper>
            <CardTitle>Why Choose ASC Coding?</CardTitle>
            <List>
              <ListItem><FaCheckCircle /> High demand for ASC coders</ListItem>
              <ListItem><FaCheckCircle /> Faster career advancement</ListItem>
              <ListItem><FaCheckCircle /> Specialized expertise = higher pay</ListItem>
              <ListItem><FaCheckCircle /> Work in surgery centers nationwide</ListItem>
              <ListItem><FaCheckCircle /> Telecommuting opportunities</ListItem>
            </List>
          </InfoCard>
        </Grid2Col>

        <SectionTitle>What You'll Learn</SectionTitle>
        <Grid3Col>
          <InfoCard>
            <IconWrapper><FaFileInvoice /></IconWrapper>
            <CardTitle>ASC Billing Rules</CardTitle>
            <CardText>Medicare coverage, payment groups, multiple procedure discounting, and modifier application specific to ASCs.</CardText>
          </InfoCard>
          <InfoCard>
            <IconWrapper><FaProcedures /></IconWrapper>
            <CardTitle>Surgical Coding</CardTitle>
            <CardText>CPT coding for common ASC procedures including orthopedics, ophthalmology, GI, pain management, and more.</CardText>
          </InfoCard>
          <InfoCard>
            <IconWrapper><FaShieldAlt /></IconWrapper>
            <CardTitle>Compliance</CardTitle>
            <CardText>ASC quality reporting, conditions for coverage, audits, and fraud prevention strategies.</CardText>
          </InfoCard>
        </Grid3Col>

        <CurriculumSection>
          <SectionTitle>Course Curriculum</SectionTitle>
          {curriculum.map((module) => (
            <ModuleCard key={module.module}>
              <ModuleHeader>
                <span style={{ background: '#ffd966', color: '#0a2b3c', padding: '0.3rem 1rem', borderRadius: '20px', fontWeight: '600' }}>
                  Module {module.module}
                </span>
                <h3>{module.title}</h3>
              </ModuleHeader>
              <ModuleContent>
                <List>
                  {module.topics.map((topic, idx) => (
                    <ListItem key={idx}>
                      <FaCheckCircle /> {topic}
                    </ListItem>
                  ))}
                </List>
              </ModuleContent>
            </ModuleCard>
          ))}
        </CurriculumSection>

        <CTASection>
          <h3>Ready to Master ASC Coding?</h3>
          <p>Join our next batch and become a certified ambulatory surgery coder</p>
          <CTAButton to="/register">
            Enroll Now <FaArrowRight />
          </CTAButton>
        </CTASection>
      </Container>
    </PageContainer>
  );
};

export default AmbulatorySurgery;